import { useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useNotify } from '@hotjar/hotsauce';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { logError } from '@hotjar/webapp/lib/logger';
import type { Organization } from '@hotjar/organization';

import { useOwnedAccount } from '@hotjar/account';
import { useSites } from '@hotjar/site';
import type { IdentityProviderModel } from '../model';
import { enableSsoOnOrganization, IDENTITY_PROVIDERS_QUERY_KEY } from '../api';

export interface UseIdpConnectionFlow {
  newConnectionState: NewConnectionState;
  isLoading: boolean;
  selectedIdp: IdentityProviderModel | null;
  selectedOrganization: Organization | null;
  selectIdpForConnection: (identityProvider: IdentityProviderModel) => void;
  selectOrganizationForConnection: (organization: Organization) => void;
  confirmConnection: () => void;
  dismissConnection: () => void;
  restartConnection: () => void;
}

export enum NewConnectionState {
  INIT = 'init',
  SELECT_ORGANIZATION = 'select_organization',
  CONFIRM = 'confirm',
  SUCCESS = 'success',
}

export function useIdpConnectionFlow(): UseIdpConnectionFlow {
  const { t } = useTranslation();
  const { showToast } = useNotify();

  const { data: account } = useOwnedAccount();
  const accountId = account?.id;
  const {
    query: { data: sites },
  } = useSites();

  const [mfaEnabledOrganizations, setMfaEnabledOrganizations] = useState<number[]>([]);
  const [selectedIdp, setSelectedIdp] = useState<IdentityProviderModel | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [newConnectionState, setNewConnectionState] = useState(NewConnectionState.INIT);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldRefetchOnDismissal, setShouldRefetchOnDismissal] = useState(false);

  const selectIdpForConnection = useCallback((identityProvider: IdentityProviderModel) => {
    setSelectedIdp(identityProvider);
    setNewConnectionState(NewConnectionState.SELECT_ORGANIZATION);
  }, []);

  const selectOrganizationForConnection = useCallback(
    (organization: Organization) => {
      if (mfaEnabledOrganizations.includes(organization.id)) {
        showToast({
          kind: 'danger',
          text: t('common:settings.sso.confirm-connection-modal.mfa-error'),
        });
        return;
      }
      setSelectedOrganization(organization);
      setNewConnectionState(NewConnectionState.CONFIRM);
    },
    [mfaEnabledOrganizations, showToast, t],
  );

  useEffect(() => {
    if (sites) {
      setMfaEnabledOrganizations(
        Array.from(
          sites.reduce((previousValue, currentValue) => {
            if (currentValue.enforcedMfaAt) {
              previousValue.add(currentValue.organizationId);
            }
            return previousValue;
          }, new Set<number>()),
        ),
      );
    }
  }, [sites]);

  const enableSsoMutation = useMutation(enableSsoOnOrganization);

  const confirmConnection = useCallback(async () => {
    if (!accountId || !selectedOrganization || !selectedIdp) {
      return;
    }

    setIsLoading(true);

    try {
      await enableSsoMutation.mutateAsync({
        accountId,
        organizationId: selectedOrganization?.id,
        identityProviderLoginId: selectedIdp?.loginId,
      });
      setNewConnectionState(NewConnectionState.SUCCESS);
      setShouldRefetchOnDismissal(true);
    } catch (error) {
      // @ts-expect-error fix catch error type
      logError(error);
      showToast({
        kind: 'danger',
        text: t('common:settings.sso.confirm-connection-modal.error'),
      });
    } finally {
      setIsLoading(false);
    }
  }, [enableSsoMutation, accountId, selectedIdp, selectedOrganization, showToast, t]);

  const dismissConnection = useCallback(() => {
    setSelectedIdp(null);
    setSelectedOrganization(null);
    setNewConnectionState(NewConnectionState.INIT);

    if (shouldRefetchOnDismissal) {
      queryClient.invalidateQueries(IDENTITY_PROVIDERS_QUERY_KEY);
    }
  }, [shouldRefetchOnDismissal]);

  const restartConnection = useCallback(() => {
    if (!selectedIdp || !selectedOrganization) {
      return;
    }

    setSelectedIdp({
      ...selectedIdp,
      organizations: [...selectedIdp.organizations, selectedOrganization],
    });
    setSelectedOrganization(null);
    setNewConnectionState(NewConnectionState.SELECT_ORGANIZATION);
  }, [selectedIdp, selectedOrganization]);

  return {
    newConnectionState,
    isLoading,
    selectedIdp,
    selectedOrganization,
    selectIdpForConnection,
    selectOrganizationForConnection,
    confirmConnection,
    dismissConnection,
    restartConnection,
  };
}
